import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, TextInput } from '../components/ui.tsx';
import { Turnstile, useAppConfig } from '../components/turnstile.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import AuthShell from '../components/auth-shell.tsx';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('auth.resetPassword.documentTitle'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  const config = useAppConfig();
  const siteKey = config.data?.turnstileSiteKey ?? null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: t('auth.resetPassword.passwordMismatch') });
      return;
    }

    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      await authApi.resetPassword({
        token,
        newPassword,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      navigate('/login', { replace: true });
    } catch (caught) {
      setTurnstileReset((value) => value + 1);
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fields);
      } else {
        setError(t('auth.resetPassword.networkError'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title={t('auth.resetPassword.title')}>
        <Alert tone="error" title={t('auth.resetPassword.incompleteTitle')}>
          {t('auth.resetPassword.incompleteMessage')}
        </Alert>
        <p className="mt-6 text-sm">
          <Link to="/forgot-password" className="text-[color:var(--color-brand)] hover:underline">
            {t('auth.resetPassword.requestNewLink')}
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.resetPassword.title')} subtitle={t('auth.resetPassword.subtitle')}>
      <form onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        <TextInput
          label={t('auth.resetPassword.newPasswordLabel')}
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint={t('auth.resetPassword.newPasswordHint')}
          value={newPassword}
          error={fieldErrors.newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <TextInput
          label={t('auth.resetPassword.confirmPasswordLabel')}
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          error={fieldErrors.confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />

        {siteKey ? <Turnstile siteKey={siteKey} onToken={setTurnstileToken} resetSignal={turnstileReset} /> : null}

        <Button type="submit" variant="primary" busy={busy} className="w-full">
          {t('auth.resetPassword.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
